import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'sonner';
import { Button } from './components/ui/button';
import { Card, CardContent } from './components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './components/ui/dialog';
import {
  Eye, Users, Image, Video, Dumbbell, Camera, FileText,
  Shield, Loader2, Info, Crown, MessageCircle
} from 'lucide-react';
import { API } from './config';

const VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Everyone', icon: Eye, description: 'Visible to all visitors' },
  { value: 'followers', label: 'Followers Only', icon: Users, description: 'Only people who follow you' },
  { value: 'subscribers', label: 'Subscribers Only', icon: Crown, description: 'Only paying subscribers' }
];

const PrivacySettingItem = ({ icon: Icon, label, description, value, onChange, options = VISIBILITY_OPTIONS }) => (
  <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-blue-200 transition-colors">
    <div className="flex items-center space-x-3">
      <div className="p-2 bg-gray-100 rounded-lg">
        <Icon className="h-5 w-5 text-gray-600" />
      </div>
      <div>
        <h4 className="font-medium text-gray-900">{label}</h4>
        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[180px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            <div className="flex items-center space-x-2">
              <option.icon className="h-4 w-4" />
              <span>{option.label}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// SIMPLIFIED PRIVACY SETTINGS MODAL
// ═══════════════════════════════════════════════════════════════════════════════
export const CreatorPrivacySettingsModal = ({ open, onClose, userId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    profile_photo_visibility: 'public',
    bio_visibility: 'public',
    workout_posts_visibility: 'public',
    featured_photos_visibility: 'public',
    featured_videos_visibility: 'public',
    comments_interactions: 'public',
  });

  useEffect(() => {
    const fetchPrivacySettings = async () => {
      try {
        setLoading(true);
        const sessionToken = Cookies.get('session_token');
        const response = await axios.get(`${API}/users/${userId}/privacy-settings`, {
          headers: { Authorization: `Bearer ${sessionToken}` }
        });
        setSettings(prev => ({ ...prev, ...response.data }));
      } catch (error) {
        console.error('Error fetching privacy settings:', error);
      } finally {
        setLoading(false);
      }
    };
    if (open && userId) fetchPrivacySettings();
  }, [open, userId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const sessionToken = Cookies.get('session_token');
      await axios.put(`${API}/users/${userId}/privacy-settings`, settings, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      toast.success('Privacy settings updated');
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const update = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Shield className="h-5 w-5 text-blue-600" />
            <span>Privacy Settings</span>
          </DialogTitle>
          <DialogDescription>Control who can see your content and interact with you</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Content Visibility */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold">Content Visibility</h3>
              </div>
              <div className="space-y-3">
                <PrivacySettingItem icon={Image} label="Profile Photo" description="Who can see your profile picture"
                  value={settings.profile_photo_visibility} onChange={(v) => update('profile_photo_visibility', v)} />
                <PrivacySettingItem icon={FileText} label="Bio" description="Who can read your bio"
                  value={settings.bio_visibility} onChange={(v) => update('bio_visibility', v)} />
                <PrivacySettingItem icon={Dumbbell} label="Workout Posts" description="Who can see your workout content"
                  value={settings.workout_posts_visibility} onChange={(v) => update('workout_posts_visibility', v)} />
                <PrivacySettingItem icon={Camera} label="Featured Photos" description="Who can see your featured photos"
                  value={settings.featured_photos_visibility} onChange={(v) => update('featured_photos_visibility', v)} />
                <PrivacySettingItem icon={Video} label="Featured Videos" description="Who can see your featured videos"
                  value={settings.featured_videos_visibility} onChange={(v) => update('featured_videos_visibility', v)} />
              </div>
            </div>

            {/* Interaction Settings */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <MessageCircle className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-semibold">Interactions</h3>
              </div>
              <div className="space-y-3">
                <PrivacySettingItem icon={MessageCircle} label="Comments" description="Who can comment on your posts"
                  value={settings.comments_interactions} onChange={(v) => update('comments_interactions', v)} />
              </div>
            </div>

            {/* Info */}
            <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-700">
                Content-level settings apply per section. Individual workout plans and meal plans have their own access controls set during creation.
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || loading} className="bg-blue-600 hover:bg-blue-700" data-testid="save-privacy-settings">
            {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving...</> : 'Save Settings'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Keep other exports for backward compatibility
export const PrivacySettingsPreview = () => null;
export const PrivacyBadge = () => null;
